/**
 * Items: everything the company buys and sells, with stock in hand.
 *
 * The list is the screen people come back to all day ("how many cartons
 * of Pepsi do we have, and what do we sell them for?"), so search sits
 * first and is focused on arrival: code, English or Arabic name, or a
 * scanned barcode -- of the piece or of the carton.
 *
 * Cost and stock value appear only for users who may see margins; the
 * server leaves them out for everyone else, this screen just follows.
 */
import { useDeferredValue, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Alert, Box, Button, MenuItem, Paper, Skeleton, Stack, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography, useMediaQuery,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import UploadIcon from '@mui/icons-material/UploadFileOutlined'
import SearchIcon from '@mui/icons-material/Search'
import { useTranslation } from 'react-i18next'

import { fetchInvGroups, fetchInvItems, fetchStores, importInvItems } from '../api/endpoints'
import { errorMessage } from '../api/client'
import { P, type InvItem } from '../api/types'
import { useAuth } from '../store/AuthContext'
import { useHotkeys } from '../hotkeys/HotkeyProvider'
import ScreenTitle from '../components/ScreenTitle'
import Keycap from '../components/Keycap'
import { Figure, FigureGrid } from '../reports/SummaryFigures'
import { useBi } from '../components/Bi'
import { c, fmtMoney } from '../theme'
import ItemDialog from './ItemDialog'
import ImportDialog from './ImportDialog'
import { fmtQty, packLabel } from './qty'

type Status = 'active' | 'inactive' | 'all'

export default function ItemsPage() {
  const { t, i18n } = useTranslation()
  const bi = useBi()
  const { can } = useAuth()
  const ar = i18n.language === 'ar'
  const narrow = useMediaQuery('(max-width:760px)')
  const mayEdit = can(P.inventoryMasters)
  const seesCost = can(P.reportFinancial)

  const [q, setQ] = useState('')
  const needle = useDeferredValue(q.trim())
  const [group, setGroup] = useState<number | ''>('')
  const [status, setStatus] = useState<Status>('active')
  const [editing, setEditing] = useState<InvItem | 'new' | null>(null)
  const [importing, setImporting] = useState(false)

  const groups = useQuery({ queryKey: ['inv-groups'], queryFn: fetchInvGroups })
  const stores = useQuery({ queryKey: ['inv-stores'], queryFn: fetchStores })
  const items = useQuery({
    queryKey: ['inv-items', needle, group, status],
    queryFn: () => fetchInvItems({
      q: needle || undefined, group_id: group || undefined,
      active: status === 'all' ? undefined : status === 'active' }),
    placeholderData: (prev) => prev,
  })

  useHotkeys('inv-items', [
    { combo: 'alt+n', label: t('inv.newItem'), handler: () => setEditing('new') },
  ], mayEdit && !editing && !importing)

  const storeName = useMemo(() => {
    const m = new Map<string, string>()
    for (const s of stores.data ?? []) m.set(String(s.id), (ar && s.name_ar) || s.name_en)
    return m
  }, [stores.data, ar])

  const rows = items.data?.items ?? []

  return (
    <Stack spacing={2}>
      <Paper variant="outlined">
        <ScreenTitle titleKey="nav.items" sub={t('inv.itemsSub')} right={mayEdit && (
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<UploadIcon />}
              onClick={() => setImporting(true)}>{t('inv.import')}</Button>
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => setEditing('new')}>
              {t('inv.newItem')}
              <Box component="span" sx={{ ml: 1, display: { xs: 'none', sm: 'inline' } }}>
                <Keycap combo="alt+n" /></Box>
            </Button>
          </Stack>
        )} />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} useFlexGap
          sx={{ px: 2.5, pt: 1, pb: 2, flexWrap: 'wrap' }}>
          <TextField autoFocus size="small" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={t('inv.searchItems')} sx={{ flex: { sm: '1 1 280px' } }}
            inputProps={{ 'aria-label': t('inv.searchItems') }}
            InputProps={{ startAdornment: <SearchIcon fontSize="small"
              sx={{ color: c.inkMuted, mr: 1 }} /> }} />
          <TextField select size="small" label={t('inv.group')} value={group}
            onChange={(e) => setGroup(e.target.value === '' ? '' : Number(e.target.value))}
            sx={{ minWidth: 180 }}>
            <MenuItem value="">{t('inv.allGroups')}</MenuItem>
            {(groups.data ?? []).map((g) => (
              <MenuItem key={g.id} value={g.id}>{(ar && g.name_ar) || g.name_en}</MenuItem>
            ))}
          </TextField>
          <ToggleButtonGroup exclusive size="small" value={status}
            onChange={(_, v) => v && setStatus(v)} aria-label={t('inv.status')}>
            <ToggleButton value="active">{t('inv.active')}</ToggleButton>
            <ToggleButton value="inactive">{t('inv.inactive')}</ToggleButton>
            <ToggleButton value="all">{t('inv.all')}</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {items.data && (
          <FigureGrid>
            <Figure label={bi('inv.itemCount')} value={String(items.data.count)} />
            <Figure label={bi('inv.groups')} value={String(groups.data?.length ?? 0)} />
            <Figure label={bi('nav.stores')} value={String(stores.data?.length ?? 0)} />
            {seesCost && items.data.stock_value !== undefined && (
              <Figure label={bi('inv.stockValue')} value={fmtMoney(items.data.stock_value)} strong />
            )}
          </FigureGrid>
        )}

        {items.isError ? (
          <Alert severity="warning" sx={{ m: 2 }}>{errorMessage(items.error)}</Alert>
        ) : items.isLoading ? (
          <Box sx={{ p: 2 }}><Skeleton /><Skeleton /><Skeleton /></Box>
        ) : rows.length === 0 ? (
          <Stack spacing={1} sx={{ px: 2.5, py: 4 }} alignItems="flex-start">
            <Typography sx={{ color: c.inkMuted }}>
              {needle || group ? t('inv.noMatch') : t('inv.noItems')}</Typography>
            {mayEdit && !needle && !group && (
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={() => setEditing('new')}>{t('inv.newItem')}</Button>
                <Button variant="outlined" onClick={() => setImporting(true)}>{t('inv.importExcel')}</Button>
              </Stack>
            )}
          </Stack>
        ) : narrow ? (
          <Box className="ruled">
            {rows.map((it) => (
              <Box key={it.id} component="button" type="button" onClick={() => setEditing(it)}
                sx={{ display: 'flex', gap: 1.5, width: '100%', px: 2, py: 1.25, border: 0,
                      bgcolor: 'transparent', color: 'inherit', font: 'inherit',
                      textAlign: 'start', cursor: 'pointer', opacity: it.is_active ? 1 : 0.55 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography dir="auto" noWrap sx={{ fontWeight: 500, fontSize: 14 }}>
                    {(ar && it.name_ar) || it.name_en}</Typography>
                  <Typography sx={{ fontSize: 12, color: c.inkMuted }} noWrap>
                    <span className="doc-no">{it.code}</span> · {packLabel(it)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'end' }}>
                  <Typography className="num" sx={{ fontWeight: 600 }}>
                    {it.sale_price ? fmtMoney(it.sale_price) : '—'}</Typography>
                  <Typography sx={{ fontSize: 12, color: Number(it.on_hand) < 0 ? c.dangerInk : c.inkMuted }}>
                    {t('inv.onHandShort', { qty: fmtQty(it.on_hand), unit: it.base_unit })}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ px: 2, pb: 1, overflowX: 'auto' }}>
            <table className="rpt" style={{ minWidth: 820 }}>
              <thead><tr>
                <th style={{ width: 110 }}>{t('inv.code')}</th>
                <th>{t('inv.item')}</th>
                <th style={{ width: 130 }}>{t('inv.group')}</th>
                <th style={{ width: 150 }}>{t('inv.units')}</th>
                <th style={{ width: 70 }}>{t('inv.vat')}</th>
                <th className="num" style={{ width: 100 }}>{t('inv.price')}</th>
                <th className="num" style={{ width: 110 }}>{t('inv.onHand')}</th>
                {seesCost && <th className="num" style={{ width: 96 }}>{t('inv.avgCost')}</th>}
                {seesCost && <th className="num" style={{ width: 110 }}>{t('inv.value')}</th>}
              </tr></thead>
              <tbody>
                {rows.map((it) => (
                  <tr key={it.id} className="clickable" onClick={() => setEditing(it)}
                    style={{ opacity: it.is_active ? 1 : 0.55 }}>
                    <td><span className="doc-no">{it.code}</span></td>
                    <td>
                      <div dir="auto" style={{ fontWeight: 500 }}>{(ar && it.name_ar) || it.name_en}</div>
                      {!ar && it.name_ar && (
                        <div dir="rtl" style={{ fontSize: 12, color: 'var(--ink-muted)',
                          textAlign: 'start' }}>{it.name_ar}</div>
                      )}
                    </td>
                    <td>{it.group_name ?? '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{packLabel(it)}</td>
                    <td>{vatShort(it.vat_category, t)}</td>
                    <td className="num">{it.sale_price ? fmtMoney(it.sale_price) : '—'}</td>
                    <td className="num">
                      <Tooltip arrow title={Object.entries(it.stock).filter(([, v]) => Number(v) !== 0)
                        .map(([sid, v]) => `${storeName.get(sid) ?? sid}: ${fmtQty(v)}`).join(' · ') || ''}>
                        <span style={{ color: Number(it.on_hand) < 0 ? 'var(--danger-ink)' : undefined }}>
                          {fmtQty(it.on_hand)} <span style={{ color: 'var(--ink-muted)', fontSize: 12 }}>
                            {it.base_unit}</span>
                        </span>
                      </Tooltip>
                    </td>
                    {seesCost && <td className="num">{Number(it.avg_cost ?? 0).toFixed(2)}</td>}
                    {seesCost && <td className="num">{fmtMoney(it.stock_value ?? '0')}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </Paper>

      {editing && (
        <ItemDialog item={editing === 'new' ? null : editing} groups={groups.data ?? []}
          stores={stores.data ?? []} editable={mayEdit} onClose={() => setEditing(null)} />
      )}
      {importing && (
        <ImportDialog title={t('inv.importItemsTitle')} help={t('inv.importItemsHelp')}
          sample={ITEM_SAMPLE} run={importInvItems} invalidate={['inv-items', 'inv-groups']}
          onClose={() => setImporting(false)} />
      )}
    </Stack>
  )
}

const ITEM_SAMPLE = `code,name_en,name_ar,group,base_unit,barcode,vat,sale_price,min_price,pack_unit,pack_factor,pack_barcode,pack_price
PEP330,Pepsi 330ml can,بيبسي ٣٣٠ مل,Beverages,PCS,6281000000011,15,2.50,2.00,CTN,24,6281000000028,55.00`

function vatShort(cat: string, t: (k: string) => string): string {
  return cat === 'STANDARD' ? '15%' : t(`inv.vat${cat}`)
}

